<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import TripCard from '../travel/TripCard.vue';
import AddPanel from '../AddPanel.vue';
import { useAuth } from '../../composables/useAuth.js';
import { useFileActions } from '../../composables/useFileActions.js';
import { useConfirm } from '../../composables/useConfirm.js';

const props = defineProps({
  player: { type: Object, required: true },
});

const { t } = useI18n();
const { can, ownerStamp } = useAuth();
const { remove } = useFileActions();
const { confirm } = useConfirm();

/* Felder der neuen Reise. Bewusst nur das Nötigste beim Anlegen — Flüge und
   Unterkunft trägt man danach in der Karte nach, oft weiß man sie beim
   Anlegen noch gar nicht. */
const draft = ref({ label: '', invitationLabel: '' });

function reset() {
  draft.value = { label: '', invitationLabel: '' };
}

function addTrip({ close }) {
  const label = draft.value.label.trim();
  if (!label) return;

  if (!props.player.trips) props.player.trips = [];
  props.player.trips.push({
    label,
    invitationLabel: draft.value.invitationLabel || '',
    outbound: { from: '', to: '', date: '', files: [] },
    returnFlight: { from: '', to: '', date: '', files: [] },
    hotel: '',
    visaFiles: [],
    updates: [],
    ...ownerStamp(),
  });
  reset();
  close();
}

async function removeTrip(index) {
  const trip = props.player.trips[index];
  if (!trip) return;

  const files = [
    ...(trip.outbound?.files || []),
    ...(trip.returnFlight?.files || []),
    ...(trip.visaFiles || []),
  ];

  const ok = await confirm({
    title: t('confirm.deleteTripTitle'),
    message: t('confirm.deleteTrip', { name: trip.label || t('travel.untitled') }),
    /* Die Zahl der Anhänge nennen: Beim Löschen einer Reise verschwinden auch
       die Flugtickets, und das ist auf den ersten Blick nicht offensichtlich. */
    detail: files.length ? t('confirm.deleteTripFiles', { count: files.length }) : '',
  });
  if (!ok) return;

  props.player.trips.splice(index, 1);
  files.forEach((f) => remove(f));
}
</script>

<template>
  <div>
    <div v-if="!(player.trips || []).length" class="feed-empty">
      {{ $t('travel.noTrips') }}
    </div>

    <TripCard
      v-for="(trip, i) in player.trips || []"
      :key="i"
      :player="player"
      :trip="trip"
      @remove="removeTrip(i)"
    />

    <AddPanel
      v-if="can('travel', 'write')"
      :label="$t('travel.addTrip')"
      :invalid="!draft.label.trim()"
      @submit="addTrip"
      @cancel="reset"
    >
      <input v-model="draft.label" type="text" :placeholder="$t('travel.titlePlaceholder')" />

      <label class="field-inline">
        <span>{{ $t('travel.connectInvitation') }}</span>
        <select v-model="draft.invitationLabel">
          <option value="">{{ $t('travel.noInvitation') }}</option>
          <option v-for="inv in player.invitations || []" :key="inv.label" :value="inv.label">
            {{ inv.label }}
          </option>
        </select>
      </label>

      <p class="add-hint">{{ $t('travel.addHint') }}</p>
    </AddPanel>
  </div>
</template>

<style scoped>
.field-inline {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field-inline span {
  font-size: 0.66rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--dim);
}
.add-hint {
  font-size: 0.74rem;
  color: var(--dim);
  line-height: 1.5;
}
input,
select {
  width: 100%;
}
</style>
